import { isObjectDuplicateInList } from "@king-ict/eupisi-ui/utils";
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  getRequestBasicInfo as getRequestBasicInfoByIdApi,
  getApplicantParentByRequestId as getApplicantParentByRequestIdApi,
  getOtherParentByRequestId as getOtherParentByRequestIdApi,
  getChildByRequestId as getChildByRequestIdApi,
  getInstitutionByRequestId as getInstitutionByRequestIdApi,
  getLocationProgramsByRequestId as getLocationProgramsByRequestIdApi,
  getRequestCriterionDataByRequestId as getRequestCriterionDataByRequestIdApi,
  getInitialConversationByRequestId as getInitialConversationByRequestIdApi,
  getDraftRequest as getDraftRequestApi,
  getExistingInitialConversation as getExistingInitialConversationApi,
  getApplicantParent as getApplicantParentApi,
  getChild,
  getChildSiblings as getChildSiblingsApi,
  getInstitutionCriteria as getInstitutionCriteriaApi,
} from "../api/kindergartenApi";
import { RootState } from "../app/rootReducer";
import InstitutionDetails from "../models/Institution/InstitutionDetails";
import InstitutionProgram from "../models/Institution/InstitutionProgram";
import ApplicantParent from "../models/Person/ApplicantParent";
import Child from "../models/Person/Child";
import OtherParent from "../models/Person/OtherParent";
import ParentNumberEnum from "../models/Person/ParentNumberEnum";
import QuestionnaireQuestionGroup from "../models/Questionnaire/QuestionnaireQuestionGroup";
import InitialConversationQuestionDataItem from "../models/Request/InitialConversationQuestionDataItem";
import InitialConversationQuestionGroup from "../models/Request/InitialConversationQuestionGroup";
import Request, { RequestInitial } from "../models/Request/Request";
import RequestCriterion from "../models/Request/RequestCriterion";
import RequestCriterionData, {
  requestCriterionDataInitial,
} from "../models/Request/RequestCriterionData";
import { RequestDocumentData } from "../models/Request/RequestDocumentData";
import RequestTypeEnum from "../models/Request/RequestTypeEnum";

interface RequestState {
  request: Request;
  criteria: RequestCriterion[];
}

let initialState: RequestState = {
  request: RequestInitial,
  criteria: [],
};

export const getRequestBasicInfo = createAsyncThunk(
  "request/getRequestBasicInfo",
  async (id: number) => {
    const response = await getRequestBasicInfoByIdApi(id);
    return response;
  }
);

export const getApplicantParentByRequestId = createAsyncThunk(
  "request/getApplicantParrentByRequestId",
  async ({
    requestId,
    refreshData,
  }: {
    requestId: number;
    refreshData?: boolean;
  }) => {
    const response = await getApplicantParentByRequestIdApi(
      requestId,
      refreshData
    );
    return response;
  }
);

export const getApplicantParentByOib = createAsyncThunk(
  "request/getApplicantParentByOib",
  async (applicantParentOib?: string) => {
    const response = await getApplicantParentApi(applicantParentOib);
    return response;
  }
);

export const getOtherParentByRequestId = createAsyncThunk(
  "request/getOtherParrentByRequestId",
  async ({
    requestId,
    refreshData,
  }: {
    requestId: number;
    refreshData?: boolean;
  }) => {
    const response = await getOtherParentByRequestIdApi(requestId, refreshData);
    return response;
  }
);

export const getChildByRequestId = createAsyncThunk(
  "request/getChildByRequestId",
  async ({
    requestId,
    refreshData,
  }: {
    requestId: number;
    refreshData?: boolean;
  }) => {
    const response = await getChildByRequestIdApi(requestId, refreshData);
    return response;
  }
);

export const getChildByOib = createAsyncThunk(
  "request/getChildByOib",
  async (oib: string) => {
    const response = await getChild(oib);
    return response;
  }
);

export const getChildSiblings = createAsyncThunk(
  "request/getChildSiblings",
  async ({
    childOib,
    applicantParentOib,
    otherParentOib,
  }: {
    childOib: string;
    applicantParentOib: string;
    otherParentOib?: string;
  }) => {
    const response = await getChildSiblingsApi(
      childOib,
      applicantParentOib,
      otherParentOib
    );
    return response;
  }
);

export const getInstitutionByRequestId = createAsyncThunk(
  "request/getInstitutionByRequestId",
  async (requestId: number) => {
    const response = await getInstitutionByRequestIdApi(requestId);
    return response;
  }
);

export const getLocationProgramsByRequestId = createAsyncThunk(
  "request/getLocationProgramsByRequestId",
  async (requestId: number) => {
    const response = await getLocationProgramsByRequestIdApi(requestId);
    return response;
  }
);

export const getRequestCriterionDataByRequestId = createAsyncThunk(
  "request/getRequestCriterionDataByRequestId",
  async (requestId: number) => {
    const response = await getRequestCriterionDataByRequestIdApi(requestId);
    return response;
  }
);

export const getInitialConversationByRequestId = createAsyncThunk(
  "requests/getInitialConversationByRequestId",
  async (requestId: number) => {
    const response = await getInitialConversationByRequestIdApi(requestId);
    return response;
  }
);

export const getDraftRequest = createAsyncThunk(
  "request/getDraftRequest",
  async (guid: string) => {
    const response = await getDraftRequestApi(guid, true);
    return response;
  }
);

export const getCriteria = createAsyncThunk<
  RequestCriterion[],
  void,
  {
    state: RootState;
  }
>("request/getCriteria", async (_, thunkApi) => {
  const requestState = thunkApi.getState().request.request;

  const institutionCriteria = await getInstitutionCriteriaApi(
    requestState.selectedInstitution.pedagogicalYearInstitutionId
  );

  return institutionCriteria.map((ic) => {
    return {
      ...ic,
      hasBeenMarkedByUser: false,
      documents: [],
    } as RequestCriterion;
  });
});

export const getExistingInitialConversation = createAsyncThunk<
  { data: InitialConversationQuestionGroup[]; shouldUpdateState: boolean },
  boolean,
  {
    state: RootState;
  }
>(
  "request/getExistingInitialConversation",
  async (shouldUpdateState, thunkApi) => {
    const state = thunkApi.getState();

    const appllicantParentOib = state.request.request.applicantParent.oib;
    const childOib = state.request.request.child.oib;

    const response = await getExistingInitialConversationApi(
      appllicantParentOib,
      childOib
    );

    return { data: response, shouldUpdateState: shouldUpdateState };
  }
);

export const selectConversationDataByQuestionGroupSort = createSelector(
  (state: RootState) => state.request.request.initialConversationQuestionGroups,
  (_: any, questionGroupSort: number) => questionGroupSort,
  (
    initialConversationQuestionGroups: InitialConversationQuestionGroup[],
    questionGroupSort: number
  ) =>
    initialConversationQuestionGroups
      .filter((qg) => qg.sort === questionGroupSort)
      .flatMap((qg) => qg.questionsData)
);

export const getPersonsOibs = createSelector(
  (state: RootState) => state.request.request,
  (request: Request) => {
    return {
      applicantParentOib: request.applicantParent.oib,
      otherParentOib: request.otherParent?.oib,
      childOib: request.child.oib,
    };
  }
);

export const selectAllDocuments = createSelector(
  (state: RootState) => state.request.request,
  (request: Request) => {
    const allDocuments: RequestDocumentData[] = [];

    request.applicantParent?.documents?.forEach((d) => {
      if (!isObjectDuplicateInList(d, allDocuments)) {
        allDocuments.push(d);
      }
    });

    request.otherParent?.documents?.forEach((d) => {
      if (!isObjectDuplicateInList(d, allDocuments)) {
        allDocuments.push(d);
      }
    });

    request.child?.documents?.forEach((d) => {
      if (!isObjectDuplicateInList(d, allDocuments)) {
        allDocuments.push(d);
      }
    });

    request.criterionData?.criteria?.forEach((c) => {
      c.documents?.forEach((d) => {
        if (!isObjectDuplicateInList(d, allDocuments)) {
          allDocuments.push(d);
        }
      });
    });

    request.initialConversationQuestionGroups?.forEach((i) => {
      i.questionsData.forEach((q) => {
        if (q.hasOwnProperty("documents")) {
          q.documents?.forEach((d) => {
            allDocuments.push(d);
          });
        }
      });
    });

    return allDocuments;
  }
);

export const selectMainInfo = createSelector(
  (state: RootState) => state.request.request,
  (request: Request) => {
    return {
      uId: request.uId,
      applicantParentOib: request.applicantParent.oib,
      childOib: request.child.oib,
    };
  }
);

const requestSlice = createSlice({
  name: "request",
  initialState: initialState,
  reducers: {
    updateRequestParentNumber(state, action: PayloadAction<ParentNumberEnum>) {
      state.request.parentNumberId = action.payload;
      if (action.payload === ParentNumberEnum.OneParent) {
        state.request.otherParent = undefined;
      }
    },
    updateRequestType(state, action: PayloadAction<RequestTypeEnum>) {
      state.request.requestTypeId = action.payload;
    },
    updateRequestApplicant(state, action: PayloadAction<ApplicantParent>) {
      state.request.applicantParent = action.payload;
    },
    updateRequestApplicantDocuments(
      state,
      action: PayloadAction<RequestDocumentData[]>
    ) {
      state.request.applicantParent.documents = action.payload;
    },
    updateRequestOtherParent(
      state = initialState,
      action: PayloadAction<OtherParent>
    ) {
      state.request.otherParent = action.payload;
    },
    updateRequestOtherParentDocuments(
      state,
      action: PayloadAction<RequestDocumentData[]>
    ) {
      if (state.request.otherParent) {
        state.request.otherParent.documents = action.payload;
      }
    },
    updateRequestChild(state, action: PayloadAction<Child>) {
      state.request.child = action.payload;
    },
    updateRequestChildDocuments(
      state,
      action: PayloadAction<RequestDocumentData[]>
    ) {
      state.request.child.documents = action.payload;
    },
    updateSelectedInstitution(
      state,
      action: PayloadAction<InstitutionDetails>
    ) {
      const isInstitutionChanged =
        state.request.selectedInstitution &&
        state.request.selectedInstitution.pedagogicalYearInstitutionId !==
          action.payload.pedagogicalYearInstitutionId;

      state.request.selectedInstitution = action.payload;
      if (isInstitutionChanged) {
        state.request.criterionData = requestCriterionDataInitial;
        state.request.institutionPrograms = [];
      }
    },
    updateRequestInstitutionPrograms(
      state,
      action: PayloadAction<InstitutionProgram[]>
    ) {
      state.request.institutionPrograms = action.payload;
    },
    updateRequestCriterionData(
      state,
      action: PayloadAction<RequestCriterionData>
    ) {
      state.request.criterionData = action.payload;
    },
    updateRequestCriterionDataItem(
      state,
      action: PayloadAction<RequestCriterion>
    ) {
      state.criteria = state.criteria.map((item) =>
        item.criterionId === action.payload.criterionId
          ? { ...item, ...action.payload }
          : item
      );
    },
    updateRequestInstitutionCriterions(
      state,
      action: PayloadAction<RequestCriterion[]>
    ) {
      state.criteria = action.payload;
    },
    updateRequestInitialConversationQuestionGroup(
      state,
      action: PayloadAction<{
        questionGroup: QuestionnaireQuestionGroup;
        initialConversationQuestionsData: InitialConversationQuestionDataItem[];
      }>
    ) {
      const groupPayload = action.payload.questionGroup;
      const dataPayload = action.payload.initialConversationQuestionsData;
      const groupState = state.request.initialConversationQuestionGroups;

      let initialConversationQuestionGroup = {
        id: groupPayload.id,
        sort: groupPayload.sort,
        name: groupPayload.name,
        questionsData: dataPayload?.map((qd) => {
          return { ...qd };
        }),
      } as InitialConversationQuestionGroup;

      initialConversationQuestionGroup.questionsData.forEach((qd) => {
        const question = groupPayload.questions.find(
          (q) => q.questionGroupQuestionId === qd.questionGroupQuestionId
        );

        if (!question) {
          return;
        }

        qd.questionName = question.name;

        if (qd.answerId && qd.answerId > 0) {
          // Resolve answer for single selection
          qd.answer = question.answers.find(
            (a) => a.id === qd.answerId
          )?.answer;
        } else if (qd.answerIds && qd.answerIds.length > 0) {
          // Resolve answer for multiple selection
          qd.answer = question.answers
            .filter((a) => qd.answerIds?.includes(a.id))
            .map((a) => a.answer)
            .join(", ");
        }
      });

      const groupStateIndex = groupState.findIndex(
        (qg) => qg.id === groupPayload.id
      );

      if (groupStateIndex === -1) {
        groupState.push(initialConversationQuestionGroup);
      } else {
        groupState[groupStateIndex] = initialConversationQuestionGroup;
      }
    },
    clearRequestData(state) {
      state.criteria = initialState.criteria;
      state.request = initialState.request;
    },
    setRequestUID(state, action: PayloadAction<string>) {
      state.request.uId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRequestBasicInfo.fulfilled, (state, { payload }) => {
        state.request.parentNumberId = payload.parentNumberId;
        state.request.status.id = payload.statusId;
        state.request.uId = payload.uId;
        state.request.createdByAdmin = payload.createdByAdmin;
      })
      .addCase(
        getApplicantParentByRequestId.fulfilled,
        (state, { payload }) => {
          state.request.applicantParent = payload;
        }
      )
      .addCase(getApplicantParentByOib.fulfilled, (state, { payload }) => {
        state.request.applicantParent = payload;
      })
      .addCase(getOtherParentByRequestId.fulfilled, (state, { payload }) => {
        state.request.otherParent = payload;
      })
      .addCase(getChildByRequestId.fulfilled, (state, { payload }) => {
        state.request.child = payload;
      })
      .addCase(getChildByOib.fulfilled, (state, { payload }) => {
        state.request.child = payload;
      })
      .addCase(getChildSiblings.fulfilled, (state, { payload }) => {
        state.request.child.siblings = payload;
      })
      .addCase(getInstitutionByRequestId.fulfilled, (state, { payload }) => {
        state.request.selectedInstitution = payload;
        state.request.selectedPedagogicalYearInstitutionId =
          payload.pedagogicalYearInstitutionId;
      })
      .addCase(
        getLocationProgramsByRequestId.fulfilled,
        (state, { payload }) => {
          state.request.institutionPrograms = payload;
        }
      )
      .addCase(
        getRequestCriterionDataByRequestId.fulfilled,
        (state, { payload }) => {
          state.request.criterionData = payload;
        }
      )
      .addCase(
        getInitialConversationByRequestId.fulfilled,
        (state, { payload }) => {
          state.request.initialConversationQuestionGroups = payload;
        }
      )
      .addCase(getDraftRequest.fulfilled, (state, { payload }) => {
        state.request = { ...state.request, ...payload };
        state.request.selectedPedagogicalYearInstitutionId =
          payload.selectedInstitution.pedagogicalYearInstitutionId;
      })
      .addCase(getCriteria.fulfilled, (state, { payload }) => {
        payload.forEach((payloadCriterion: RequestCriterion) => {
          let existingCriterion = state.criteria.find(
            (x) => x.criterionId === payloadCriterion.criterionId
          );
          if (existingCriterion) {
            existingCriterion = {
              ...payloadCriterion,
              hasBeenMarkedByUser: existingCriterion.hasBeenMarkedByUser,
            };
          } else {
            existingCriterion = {
              ...payloadCriterion,
            };
            state.criteria.push(existingCriterion);
          }
        });
      })
      .addCase(
        getExistingInitialConversation.fulfilled,
        (state, { payload }) => {
          if (payload.shouldUpdateState) {
            state.request.initialConversationQuestionGroups = payload.data;
          }
        }
      );
  },
});

export const {
  updateRequestParentNumber,
  updateRequestType,
  updateRequestApplicant,
  updateRequestApplicantDocuments,
  updateRequestOtherParent,
  updateRequestOtherParentDocuments,
  updateRequestChild,
  updateRequestChildDocuments,
  updateSelectedInstitution,
  updateRequestInstitutionPrograms,
  updateRequestCriterionData,
  updateRequestCriterionDataItem,
  updateRequestInstitutionCriterions,
  updateRequestInitialConversationQuestionGroup,
  clearRequestData,
  setRequestUID,
} = requestSlice.actions;

export default requestSlice.reducer;
